import { CloseOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { DataNode, EventDataNode } from "antd/es/tree";
import React, { Key, useEffect, useRef, useState } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { MenuNode } from "src/model/MenuNode";
import { itemsToNodes } from "src/routing/NavigateRoute";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";
import { searchMenuGET } from "./LeftMenuSearchAPI";

interface Props {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadedKeys: React.Dispatch<React.SetStateAction<Key[]>>;
  setExtendednKeys: React.Dispatch<React.SetStateAction<Key[]>>;
  reloadTree: () => void;
  onSelect: (keys: React.Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode<DataNode>;
      selectedNodes: MenuNode[];
      nativeEvent: MouseEvent;
    }) => void;
}

const LeftMenuSearch = ({ setIsLoading, setLoadedKeys, onSelect, setExtendednKeys }: Props) => {

  const leftMenuKey = useAppGlobStore(state => state.leftMenu?.key);
  const savedTree = useRef<MenuNode[]>(null);

  const onFieldClear = () => {
    if (savedTree.current) {
      const selectedNode = GLOB.selectedItem;
      setLoadedKeys([]);
      setExtendednKeys([]);
      useAppGlobStore.setState(state => ({
        leftMenuExpandedKeys: [],
        leftMenuSelectedKeys: selectedNode ? [selectedNode.key] : [],
        leftMenu: { ...state.leftMenu, children: savedTree.current }
      }));
      savedTree.current = null;
    }
  };

  const [searchText, setSearchText] = useState(null);

  const walkNodeTree = (node: MenuNode[], expandedKeys: Key[], selectedKeys: Key[]) => {
    for (const item of node) {
      if (item.children) {
        expandedKeys.push(item.key);
        walkNodeTree(item.children, expandedKeys, selectedKeys);
      } else {
        selectedKeys.push(item.key);
      }
    }
  };

  const selectFirstLowerMostNode = (node: MenuNode) => {
    if (node.children) {
      const nodeWithChildren = node.children.find(child => child.children);
      return selectFirstLowerMostNode(nodeWithChildren ? nodeWithChildren : node.children[0]);
    } else {
      return node;
    }
  };

  useEffect(() => {
    setSearchText("");
    savedTree.current = null;
  }, [leftMenuKey]);

  const search = async (value: string) => {
    if (!value || value === "") {
      onFieldClear();
      return;
    }
    setIsLoading(true);
    try {
      if (!savedTree.current) {
        savedTree.current = useAppGlobStore.getState().leftMenu?.children;
      }
      useAppGlobStore.setState({ leftMenuExpandedKeys: [], leftMenuSelectedKeys: [] });
      setLoadedKeys([]);
      const data = await searchMenuGET(value);
      const expandedKeys = [];
      const selectedKeys = [];
      const nodes = itemsToNodes(data);
      walkNodeTree(nodes, expandedKeys, selectedKeys);
      const toSelect = nodes?.[0] ? selectFirstLowerMostNode(nodes[0]) : null;
      if (toSelect && GLOB.selectedItem !== toSelect) onSelect(null, { selectedNodes: [toSelect], event: 'select', nativeEvent: null, node: null, selected: null });

      useAppGlobStore.setState({ leftMenuExpandedKeys: [...expandedKeys] });
      setExtendednKeys([...expandedKeys]);
      if (toSelect) {
        useAppGlobStore.setState({ leftMenuSelectedKeys: [toSelect.key] });
      }
      //const tree = GLOB.getTree();
      //console.log(tree);
      useAppGlobStore.setState(state => ({ leftMenu: { ...state.leftMenu, children: nodes } }));
    } catch (error) {
      console.error(error);
      Log.error(error.toString());
    }
    setIsLoading(false);
  };

  return (
    <Input.Search
      className='item-search'
      size="small"
      allowClear={{
        clearIcon: <CloseOutlined onClick={onFieldClear} />
      }}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      onSearch={(value) => search(value)}
    >
    </Input.Search>
  );
};

export default LeftMenuSearch;
